/**
 * Email Compose Component
 * Modern email composition with attachment support
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Subject as SubjectIcon
} from '@mui/icons-material';

import { EmailData } from '../../services/emailService';
import { formatDateTime } from '../../utils/dateUtils';

// Styled components
const ComposeContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  right: 20,
  width: '600px',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[8],
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  zIndex: 1300
}));

const ComposeHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`
}));

const ComposeContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  flex: 1,
  overflow: 'auto'
}));

const AttachmentChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5, 0.5, 0, 0)
}));

interface Attachment {
  file: File;
  id: string;
}

interface EmailComposeProps {
  open: boolean;
  onClose: () => void;
  onSent?: (email: EmailData) => void;
  replyToEmail?: EmailData;
  mode?: 'compose' | 'reply' | 'forward';
  recipientSuggestions?: string[];
}

const EmailCompose: React.FC<EmailComposeProps> = ({
  open,
  onClose,
  onSent,
  replyToEmail,
  mode = 'compose',
  recipientSuggestions = []
}) => {
  const [toAddresses, setToAddresses] = useState<string[]>(
    mode === 'reply' && replyToEmail ? [replyToEmail.from_address] : []
  );
  const [ccAddresses, setCcAddresses] = useState<string[]>([]);
  const [bccAddresses, setBccAddresses] = useState<string[]>([]);
  const [subject, setSubject] = useState(
    mode === 'reply' && replyToEmail?.subject 
      ? `Re: ${replyToEmail.subject.replace(/^Re:\s*/, '')}`
      : mode === 'forward' && replyToEmail?.subject
      ? `Fwd: ${replyToEmail.subject.replace(/^Fwd:\s*/, '')}`
      : ''
  );
  const [bodyText, setBodyText] = useState(
    mode === 'reply' && replyToEmail
      ? `\n\n--- Original Message ---\nFrom: ${replyToEmail.from_address}\nDate: ${formatDateTime(new Date(replyToEmail.email_date))}\nSubject: ${replyToEmail.subject}\n\n${replyToEmail.body_text}`
      : mode === 'forward' && replyToEmail
      ? `\n\n--- Forwarded Message ---\nFrom: ${replyToEmail.from_address}\nTo: ${replyToEmail.to_addresses.join(', ')}\nDate: ${formatDateTime(new Date(replyToEmail.email_date))}\nSubject: ${replyToEmail.subject}\n\n${replyToEmail.body_text}`
      : ''
  );
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  const handleFileAttachment = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const newAttachments = files.map(file => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 8)}`
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
    
    // Clear the input
    event.target.value = '';
  };

  const removeAttachment = (attachmentId: string) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  const validateForm = (): boolean => {
    console.log('Validating form:', { 
      toAddresses, 
      toAddressesLength: toAddresses.length, 
      subject: subject.trim(), 
      bodyText: bodyText.trim() 
    });
    
    if (toAddresses.length === 0) {
      setError('Please add at least one recipient');
      return false;
    }
    if (!subject.trim()) {
      setError('Please add a subject');
      return false;
    }
    if (!bodyText.trim()) {
      setError('Please add a message');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSend = async () => {
    // use env var or fallback to proxy path for API
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v3';
    console.log('handleSend called', { toAddresses, subject, bodyText });
    if (!validateForm()) return;

    setSending(true);
    setError(null);

    try {
      // Use FormData approach for all emails (consistent with backend)
      const formData = new FormData();
      
      const emailData = {
        to: toAddresses,
        cc: ccAddresses,
        bcc: bccAddresses,
        subject,
        textBody: bodyText,
        htmlBody: '', // Could add rich text editor support later
      };

      formData.append('emailData', JSON.stringify(emailData));

      // Add attachments if any
      attachments.forEach(attachment => {
        formData.append('attachments', attachment.file);
      });

      const response = await fetch(`${API_BASE_URL}/emails`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      const result = await response.json();

      if (result.success && onSent) {
        onSent(result.data);
      }

      onClose();
    } catch (error: any) {
      console.error('Error sending email:', error);
      setError(error.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <ComposeContainer>
      <ComposeHeader>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {mode === 'reply' ? 'Reply' : mode === 'forward' ? 'Forward' : 'New Message'}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </ComposeHeader>

      <ComposeContent>
        {/* Debug Info */}
        {process.env.NODE_ENV === 'development' && (
          <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.100', fontSize: '0.75rem' }}>
            Debug: toAddresses={JSON.stringify(toAddresses)}, subject="{subject}", bodyText.length={bodyText.length}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Recipients */}
        <Stack spacing={2}>
          <Autocomplete
            multiple
            freeSolo
            value={toAddresses}
            onChange={(_, newValue) => {
              console.log('Autocomplete onChange:', newValue);
              setToAddresses(newValue as string[]);
            }}
            onInputChange={(_, inputValue, reason) => {
              console.log('Autocomplete onInputChange:', { inputValue, reason });
              // Handle when user presses Enter or comma to add email
              if (reason === 'input' && (inputValue.includes(',') || inputValue.includes(';'))) {
                const emails = inputValue.split(/[,;]/).map(email => email.trim()).filter(email => email);
                setToAddresses(prev => [...prev, ...emails]);
              }
            }}
            options={recipientSuggestions}
            renderInput={(params) => (
              <TextField
                {...params}
                label="To"
                placeholder="Enter email addresses (press Tab or Enter to add)"
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      {params.InputProps.startAdornment}
                    </>
                  )
                }}
                onKeyDown={(e) => {
                  // Handle Enter or Tab key to add email
                  if (e.key === 'Enter' || e.key === 'Tab') {
                    const target = e.target as HTMLInputElement;
                    if (target.value && target.value.trim()) {
                      e.preventDefault();
                      const email = target.value.trim();
                      if (email && !toAddresses.includes(email)) {
                        console.log('Adding email via keydown:', email);
                        setToAddresses(prev => [...prev, email]);
                        // Clear the input by updating the autocomplete
                        setTimeout(() => {
                          target.value = '';
                        }, 0);
                      }
                    }
                  }
                }}
                onBlur={(e) => {
                  // Handle when user clicks away - auto-add the typed email
                  const target = e.target as HTMLInputElement;
                  if (target.value && target.value.trim()) {
                    const email = target.value.trim();
                    if (email && !toAddresses.includes(email)) {
                      console.log('Adding email via blur:', email);
                      setToAddresses(prev => [...prev, email]);
                    }
                  }
                }}
              />
            )}
          />

          {/* CC/BCC Toggle */}
          <Box>
            {!showCc && !showBcc && (
              <Box>
                <Button size="small" onClick={() => setShowCc(true)}>Add CC</Button>
                <Button size="small" onClick={() => setShowBcc(true)}>Add BCC</Button>
              </Box>
            )}

            {showCc && (
              <Autocomplete
                multiple
                freeSolo
                value={ccAddresses}
                onChange={(_, newValue) => setCcAddresses(newValue)}
                options={recipientSuggestions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="CC"
                    placeholder="Enter CC email addresses"
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            )}

            {showBcc && (
              <Autocomplete
                multiple
                freeSolo
                value={bccAddresses}
                onChange={(_, newValue) => setBccAddresses(newValue)}
                options={recipientSuggestions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="BCC"
                    placeholder="Enter BCC email addresses"
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            )}
          </Box>

          {/* Subject */}
          <TextField
            fullWidth
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: <SubjectIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Attachments */}
        {attachments.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Attachments ({attachments.length})
            </Typography>
            <Box>
              {attachments.map((attachment) => (
                <AttachmentChip
                  key={attachment.id}
                  label={attachment.file.name}
                  onDelete={() => removeAttachment(attachment.id)}
                  deleteIcon={<DeleteIcon />}
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Message Body */}
        <TextField
          fullWidth
          multiline
          minRows={8}
          maxRows={12}
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
          placeholder="Write your message..."
          variant="outlined"
          sx={{ mb: 2 }}
        />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <input
              accept="*/*"
              style={{ display: 'none' }}
              id="attachment-upload"
              type="file"
              multiple
              onChange={handleFileAttachment}
            />
            <label htmlFor="attachment-upload">
              <IconButton component="span" color="primary">
                <AttachFileIcon />
              </IconButton>
            </label>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={handleSend}
              disabled={sending}
            >
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </Box>
        </Box>
      </ComposeContent>
    </ComposeContainer>
  );
};

export default EmailCompose;
